import React from 'react';
import { useParams } from 'react-router';
import ArchiveLeagueDangerZone from '../components/ArchiveLeagueDangerZone';
import ComingSoonPanel from '../components/ComingSoonPanel';
import DiscordIdentityPanel from '../components/DiscordIdentityPanel';
import LeagueSetupSettingsCard from '../components/LeagueSetupSettingsCard';
import LeagueBillingPanel from '../components/LeagueBillingPanel';
import LeagueLogoPanel from '../components/LeagueLogoPanel';
import LeagueVisibilityPanel from '../components/LeagueVisibilityPanel';
import ManageCommissionersPanel from '../components/ManageCommissionersPanel';
import { useLeague } from '../hooks/useLeague';
import { leagueVisibilityLabel } from '../leagueVisibility';
import { COMMISSIONER_STAFF_COPY, OWNER_ONLY_SETTINGS_COPY } from '../leagueRoles';
import { isLeagueArchived } from '../lifecycle';
import { lscReturnTo } from '../routing';

const LscSettingsGeneralPage: React.FC = () => {
  const {
    league,
    isOperator,
    isOwner,
    refreshLeague,
    refreshLeagues,
  } = useLeague();
  const { leagueId } = useParams<{ leagueId: string }>();

  const archived = league ? isLeagueArchived(league.status) : false;

  return (
    <div className="space-y-6" data-testid="league-settings-general">
      <DiscordIdentityPanel returnTo={lscReturnTo(`/leagues/${leagueId}/settings`)} />

      {league && (
        <div className="card space-y-3 text-sm">
          <h2 className="font-semibold text-white">League details</h2>
          <dl className="grid gap-2">
            <div className="flex justify-between gap-4">
              <dt className="text-secondary-500">Name</dt>
              <dd className="text-secondary-200">{league.name}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-secondary-500">Slug</dt>
              <dd className="text-secondary-200">{league.slug}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-secondary-500">Status</dt>
              <dd className="text-secondary-200 capitalize">{league.status ?? 'active'}</dd>
            </div>
            {league.archived_at && (
              <div className="flex justify-between gap-4">
                <dt className="text-secondary-500">Archived at</dt>
                <dd className="text-secondary-200">{league.archived_at}</dd>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <dt className="text-secondary-500">Competition</dt>
              <dd className="text-secondary-200">{league.game_label ?? league.game_key}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-secondary-500">Visibility</dt>
              <dd className="text-secondary-200">{leagueVisibilityLabel(league.visibility)}</dd>
            </div>
          </dl>
        </div>
      )}

      {isOperator && !archived && leagueId ? (
        <LeagueSetupSettingsCard leagueId={leagueId} />
      ) : null}

      {isOperator && !isOwner ? (
        <div className="card space-y-2 text-sm text-secondary-400" data-testid="commissioner-settings-note">
          <p>{COMMISSIONER_STAFF_COPY}</p>
          <p>{OWNER_ONLY_SETTINGS_COPY}</p>
        </div>
      ) : null}

      {league && isOperator && (
        <LeagueLogoPanel
          league={league}
          leagueArchived={archived}
          onUpdated={async () => {
            await refreshLeague();
            await refreshLeagues();
          }}
        />
      )}

      {league && isOwner && leagueId && !archived && (
        <LeagueBillingPanel leagueId={leagueId} leagueArchived={archived} />
      )}

      {league && isOwner && (
        <ManageCommissionersPanel
          leagueId={leagueId!}
          ownerUid={league.commissioner_uid}
        />
      )}

      {league && isOwner && (
        <LeagueVisibilityPanel
          league={league}
          leagueArchived={archived}
          onUpdated={async () => {
            await refreshLeague();
            await refreshLeagues();
          }}
        />
      )}

      {league && (
        <ArchiveLeagueDangerZone
          league={league}
          isOwner={isOwner}
          onArchived={async () => {
            await refreshLeagues();
          }}
          onRestored={async () => {
            await refreshLeague();
            await refreshLeagues();
          }}
        />
      )}

      {!archived && (
        <div className="card space-y-4 opacity-70">
          <h2 className="font-semibold text-white">Coming soon</h2>
          <ul className="text-sm text-secondary-400 space-y-2 list-disc list-inside">
            <li>Twitch embed parent domain</li>
            <li>VOD duration bounds</li>
          </ul>
        </div>
      )}

      {!archived && <ComingSoonPanel />}
    </div>
  );
};

export default LscSettingsGeneralPage;
